/*
 * portl-video-poster.js
 * ──────────────────────────────────────────────────────────────────
 * Companion to snippets/portl-video-poster.liquid.
 *
 * The <picture class="portl-video-poster"> overlay chooses desktop vs
 * mobile purely via a CSS media query (no JS needed for that). This
 * script's ONLY job is to FADE THE POSTER OUT once the sibling <video>
 * actually starts rendering frames — and to bring it back if the video
 * is reset or errors. It never touches src, autoplay, mute, loop, HLS,
 * or any playback logic.
 *
 * USAGE: drop {% render 'portl-video-poster' %} as a sibling of a
 * <video>, then include this script once per page (theme.liquid):
 *   <script src="{{ 'portl-video-poster.js' | asset_url }}" defer></script>
 *
 * Sections that mount videos dynamically can re-scan:
 *   window.PortlVideoPoster.scan(rootEl)
 * ──────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || !window.document) return;

  function bind(poster) {
    if (poster.__portlPosterBound) return;
    var parent = poster.parentElement;
    if (!parent) return;
    var video = parent.querySelector('video');
    if (!video) return;
    poster.__portlPosterBound = true;

    /* The overlay is position:absolute; inset:0, so the parent must be a
       positioning context. Promote it if it's static — this lets the snippet
       drop into any section without a per-section CSS change. */
    if (window.getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    function hide() { poster.classList.add('is-hidden'); }
    function show() { poster.classList.remove('is-hidden'); }

    /* Fade the poster out as soon as the video is actually rendering
       frames. `playing` fires on real playback start; the timeupdate
       guard covers browsers that begin advancing currentTime slightly
       before firing `playing`. */
    video.addEventListener('playing', hide);
    video.addEventListener('timeupdate', function onTime() {
      if (video.currentTime > 0) { hide(); video.removeEventListener('timeupdate', onTime); }
    });

    /* Bring the poster back if the source is torn down / reloaded or a
       load error occurs — so blocked-autoplay, failed loads, and
       decoder-release (lazy) all correctly show the poster again. */
    video.addEventListener('emptied', show);
    video.addEventListener('error', show);
    video.addEventListener('pause', function () {
      /* Only re-show if playback hasn't produced a frame yet (e.g.
         autoplay was blocked). A user/scroll pause mid-playback keeps
         the current frame, so we leave the poster hidden. */
      if (video.currentTime === 0) show();
    });

    /* Already playing when we bind (e.g. re-scan after a section mount). */
    if (!video.paused && video.currentTime > 0) hide();
  }

  function scan(root) {
    var nodes = (root || document).querySelectorAll('[data-portl-video-poster]');
    Array.prototype.forEach.call(nodes, bind);
  }

  /* ── Responsive NATIVE poster (for stacked / multi-video sections) ──
     A <picture> overlay assumes one video per positioned wrapper. Sections
     that stack many videos in one container (tabs, reels, pinned heroes)
     instead put both posters on each <video> itself:
        <video data-portl-poster-desktop="…" data-portl-poster-mobile="…">
     and we swap the native `poster` attribute by viewport. Per-video, no DOM
     restructure, and the native poster already covers every load/fail state. */
  var MOBILE_MQ = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

  function applyResponsivePoster(video) {
    var d = video.getAttribute('data-portl-poster-desktop');
    var m = video.getAttribute('data-portl-poster-mobile');
    var isMobile = MOBILE_MQ ? MOBILE_MQ.matches : false;
    var want = (isMobile && m) ? m : (d || m);
    if (want && video.getAttribute('poster') !== want) video.setAttribute('poster', want);
  }

  function scanPosterVideos(root) {
    var vids = (root || document).querySelectorAll('video[data-portl-poster-desktop], video[data-portl-poster-mobile]');
    Array.prototype.forEach.call(vids, applyResponsivePoster);
  }

  if (MOBILE_MQ) {
    var onMq = function () { scanPosterVideos(); };
    if (MOBILE_MQ.addEventListener) MOBILE_MQ.addEventListener('change', onMq);
    else if (MOBILE_MQ.addListener) MOBILE_MQ.addListener(onMq);
  }

  function scanAll(root) { scan(root); scanPosterVideos(root); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanAll(); });
  } else {
    scanAll();
  }

  /* Shopify theme editor — re-scan a reloaded section. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) scanAll(e.target);
  });

  window.PortlVideoPoster = { scan: scan, attach: bind, scanPosterVideos: scanPosterVideos };
})();
